import React from 'react';
import { Icon } from '../Icon/Icon.jsx';
import { RevalidateDot } from '../RevalidateDot/RevalidateDot.jsx';
import { Tooltip } from '../Tooltip/Tooltip.jsx';
import { fx } from '../../fx.js';
import { BODY, ink } from '../../primitives.js';

const h = React.createElement;

/**
 * The button. Default is card-on-ink; `solid` is the lime primary; `danger` coral tint; `ghost`
 * borderless. Press stamps it into the page. `burst` fires GRYD-geometry confetti from the button
 * (publish-grade moments only), trembles on hover and leans toward the cursor (`magnetic`).
 *
 * @param {object} p
 * @param {React.ReactNode} [p.why]
 * @param {string} [p.icon]
 * @param {boolean} [p.loading]
 * @param {boolean} [p.burst]
 * @param {boolean} [p.magnetic]
 * @param {React.ReactNode} [p.children]
 * @param {boolean} [p.solid]
 * @param {boolean} [p.danger]
 * @param {boolean} [p.ghost]
 * @param {'sm'|'md'} [p.size]
 * @param {boolean} [p.done]
 * @param {boolean} [p.disabled]
 * @param {React.MouseEventHandler} [p.onClick]
 * @param {string} [p.title]
 * @param {React.CSSProperties} [p.style]
 * @param {'button'|'submit'} [p.type]
 */
export function Btn({ children, solid, danger, ghost, size = 'md', done, disabled, onClick, title, style, type = 'button', burst, magnetic, icon, loading, why, ...aria }) {
  if (loading) disabled = true;
  const sm = size === 'sm';
  const ref = React.useRef(null);
  const mag = (magnetic != null ? magnetic : !!burst) && !disabled;
  const move = (e) => { const f = fx; if (!mag || !f.finePointer || !f.finePointer() || f.reduced()) return; const el = ref.current, r = el.getBoundingClientRect(); const dx = (e.clientX - r.left - r.width / 2) / r.width, dy = (e.clientY - r.top - r.height / 2) / r.height; el.style.translate = `${(dx * 6).toFixed(1)}px ${(dy * 4).toFixed(1)}px`; el.style.rotate = `${(dx * 2).toFixed(2)}deg`; };
  const leave = () => { const el = ref.current; if (!el || !el.style.translate) return; const f = fx; f.animate && f.animate(el, [{ translate: el.style.translate, rotate: el.style.rotate || '0deg' }, { translate: '0px 0px', rotate: '0deg' }], { duration: 260, easing: 'cubic-bezier(.34,1.56,.64,1)' }); el.style.translate = ''; el.style.rotate = ''; };
  const enter = () => { if (burst && !disabled && fx.tremble) fx.tremble(ref.current); };
  const click = (e) => { if (disabled) return; if (burst && fx.burst) fx.burst(ref.current); onClick && onClick(e); };
  const bg = done ? 'var(--lime)' : danger ? 'var(--coral-bg)' : solid ? 'var(--go)' : ghost ? 'transparent' : 'var(--card)';
  const fg = done ? 'var(--ink)' : danger ? 'var(--coral-tx)' : solid ? 'var(--ongo)' : 'var(--tx)';
  const el = h('button', {
    ...aria, ref, type, onClick: click, 'aria-busy': loading || undefined, onPointerMove: mag ? move : undefined, onPointerLeave: mag ? leave : undefined, onPointerEnter: burst ? enter : undefined, title, 'aria-disabled': disabled || undefined, disabled,
    className: (disabled ? '' : 'mo-lift mo-stamp') + (done ? ' mo-done' : ''),
    style: {
      display: 'inline-flex', alignItems: 'center', gap: icon || loading ? 7 : 0, whiteSpace: 'nowrap',
      padding: sm ? '5px 11px' : '8px 16px', fontFamily: BODY, fontWeight: 800, fontSize: sm ? 11.5 : 13, lineHeight: 1.2,
      background: bg, color: fg, border: ghost ? '2px solid transparent' : ink(2), borderRadius: sm ? 8 : 10,
      boxShadow: ghost ? 'none' : 'var(--el-1)', cursor: disabled ? 'default' : 'pointer', opacity: disabled && !loading ? .55 : 1,
      userSelect: 'none', ...style
    }
  }, loading && RevalidateDot ? h(RevalidateDot, { size: sm ? 11 : 13, title: 'Working' }) : icon && Icon ? h(Icon, { name: icon, size: sm ? 14 : 16, stroke: 2.4 }) : null, done ? h(React.Fragment, null, '✓ ', children) : children);
  return why && disabled && Tooltip ? h(Tooltip, { label: why }, h('span', { tabIndex: 0, style: { display: 'inline-flex', borderRadius: sm ? 8 : 10 } }, el)) : el;
}
